import type { ApolloClient } from 'apollo-client';
import type { GraphQLTaggedNode } from 'relay-runtime';
import type { GraphQLError } from 'graphql';

import type { QueryInteropSupportedArgs } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';
import { isExpectedApolloError } from '@confluence/error-types/entry-points/expectedGraphQlErrorHelper.ts';
import { toDocumentNode } from '@confluence/apollo-relay-interop/entry-points/toDocumentNode.ts';
import {
	writeQueryInterop,
	type WriteQueryInteropArgs,
} from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { fg } from '@confluence/feature-gating';
import { isRelayEnabled } from '@confluence/apollo-relay-interop/entry-points/isRelayEnabled.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { getQueryBoundary } from '@confluence/query-preloader-boundary/entry-points/queryBoundaryStorage.ts';

import { getOperationName, UNEXPECTED_RESPONSE } from './getOperationName.ts';
import { getQueryName } from './getQueryName.ts';
import { isUnauthorizedError } from './errors/isUnauthorizedError.ts';
import { isNotFoundError } from './errors/isNotFoundError.ts';
import { isNotFoundSpaceError } from './errors/isNotFoundSpaceError.ts';
import { trackPreloadedQuery } from './developmentQueryBoundaryVerification.ts';
import { getAtlAttributionHeader } from './getAtlAttributionHeader.ts';

const muteExpectedGraphQLErrors = (result: any) => {
	// Preloading is "fire and forget" operation on a client as well, so we can mute
	// some known errors, but we can't really do anything about them as they're not
	// make it to the Apollo cache anyway and therefore don't pop at where they can
	// be handled.
	const hasErrors = result?.errors?.length > 0;
	result?.errors?.forEach((error: any) => {
		if (
			isUnauthorizedError(error) ||
			isExpectedApolloError(error) ||
			isNotFoundSpaceError(error) ||
			isNotFoundError(error)
		) {
			markErrorAsHandled(error);
		}
	});

	return hasErrors;
};

/**
 * Same as Apollo's query function but only used in preloader functions
 */
export function query<Q, V>({
	query: _query,
	variables,
	/**
	 * How to preload with fresh data correctly.
	 * In preloader: query with fetchPolicy: "network-only"
	 */
	fetchPolicy = 'cache-first',
	errorPolicy = 'all',
	context,
	client = window.GLOBAL_APOLLO_CLIENT,
	__callerPackage__,
}: Omit<QueryInteropSupportedArgs<Q, V & Record<string, any>>, 'query'> & {
	query: GraphQLTaggedNode;
	client?: ApolloClient<any>;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
}): Promise<{
	data: Q | null;
	errors?: ReadonlyArray<GraphQLError>;
	hasErrors: boolean;
}> {
	const query = toDocumentNode(_query);
	const name = getOperationName(query);

	// Detect if this is an agg call by checking if the client is the AGG client
	// The AGG client is created with createWrappedFetch({ isForAggOnly: true })
	const isForAggOnly =
		client === window.GLOBAL_APOLLO_AGG_CLIENT || (client as any)?.__isAGGClient === true;

	if (
		process.env.CLOUD_ENV === 'dev' ||
		process.env.CLOUD_ENV === 'branch' ||
		process.env.CLOUD_ENV === 'staging'
	) {
		const boundaryName = getQueryBoundary();
		if (boundaryName) {
			trackPreloadedQuery(query, variables, boundaryName);
		}
	}

	// This is indirect reference to next/packages/graphql/src/links/DevOnlyGraphqlErrorGenerator.ts
	if (process.env.NODE_ENV !== 'production' && (window as any).graphqlOperationController) {
		const operationController = (window as any).graphqlOperationController;
		const { shouldGenerateError } = operationController.onOperationRequested(name);
		if (shouldGenerateError) {
			return Promise.resolve(UNEXPECTED_RESPONSE);
		}
	}

	let single;
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR && !fg('new_query_batching_feedback_loop_fg')) {
		single = true;

		const queryName = getQueryName(name, variables, true);
		const { batchQueries } = getSSRRenderInputs();
		const queriesExcludedFromAggMigration =
			StatsigConfigurations.getStringList('cc-agg-migration-excluded-queries', []) || [];
		const isExcludedFromAggMigration = name
			? queriesExcludedFromAggMigration.includes(name)
			: false;
		const shouldApplyAggHeaders = fg('cc-agg-migration-use-agg-endpoint')
			? !isExcludedFromAggMigration
			: isForAggOnly;
		// Apply the same prefix logic as createWrappedFetch.ts to match batchQueries keys
		// batchQueries contains keys like "agg_QueryName" when shouldApplyAggHeaders is true
		const batchQueryKey = shouldApplyAggHeaders ? `agg_${queryName}` : queryName;
		if (batchQueryKey in (batchQueries || {})) {
			single = false;
		}
	}

	const queryContext: Record<string, any> = {
		single,
		...context,
	};

	// Only add atl-attribution header if AGG migration feature gate is enabled
	if (fg('cc-agg-migration-use-agg-endpoint')) {
		queryContext.headers = {
			...(context?.headers || {}),
			...getAtlAttributionHeader(),
		};
	}

	if (window.__SSR_REACT_STREAMING__) {
		queryContext.preload = true;
		queryContext.originalQuery = query;
	}

	if (fg('confluence_preloader_query_interop')) {
		return queryInterop<Q, V & Record<string, any>>({
			query: _query,
			variables: variables as V & Record<string, any>,
			errorPolicy,
			fetchPolicy,
			context: queryContext,
			interopApolloAggClient: isForAggOnly,
			__callerPackage__,
		}).then((result) => {
			const errors = result.error?.graphQLErrors as ReadonlyArray<GraphQLError> | undefined;
			const interopResult = { data: result.data, errors };
			const hasErrors = muteExpectedGraphQLErrors(interopResult);
			return { data: result.data ?? null, errors, hasErrors };
		});
	}

	if (!client) {
		// Referencing to the client via global variable to avoid circular dependency
		// This should never happen and will be removed in the future
		return Promise.resolve(UNEXPECTED_RESPONSE);
	}

	// `client.query` only accepts the narrower `FetchPolicy` (no `cache-and-network`/`standby`).
	// Map the watch-only values to the closest one-shot equivalent.
	const oneShotFetchPolicy =
		fetchPolicy === 'cache-and-network' || fetchPolicy === 'standby' ? 'cache-first' : fetchPolicy;

	// TODO: CCE2EPERF-926 needs to be updated to use query interop once we are on a single Apollo client
	return client
		.query({
			query,
			variables,
			errorPolicy,
			fetchPolicy: oneShotFetchPolicy,
			context: queryContext,
		})
		.then((result) => {
			const hasErrors = muteExpectedGraphQLErrors(result);
			return { ...result, hasErrors };
		});
}

export function queryAgg<T, V>(
	options: Omit<QueryInteropSupportedArgs<T, V & Record<string, any>>, 'query'> & {
		query: GraphQLTaggedNode;
		/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
		__callerPackage__?: string;
	},
) {
	return query<T, V>({
		...options,
		query: options.query,
		client: window.GLOBAL_APOLLO_AGG_CLIENT,
	});
}

/**
 * @deprecated use writeQueryInterop from `@confluence/apollo-relay-interop/entry-points/writeQuery`
 */
export function writeQuery<T, V extends Record<string, any>>(
	options: WriteQueryInteropArgs<T, V> & {
		client?: ApolloClient<any>;
		/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
		__callerPackage__?: string;
	},
) {
	if (isRelayEnabled(options.__callerPackage__)) {
		writeQueryInterop<T, V>({ ...options, __callerPackage__: options.__callerPackage__ });
	} else {
		const client = options.client || window.GLOBAL_APOLLO_CLIENT;
		if (client) {
			client.writeQuery({
				...options,
				query: toDocumentNode(options.query),
			});
		}
	}
}

/**
 * @deprecated use writeQueryInterop from `@confluence/apollo-relay-interop/entry-points/writeQuery`
 */
export function writeAggQuery<T, V extends Record<string, any>>(
	options: WriteQueryInteropArgs<T, V> & {
		/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
		__callerPackage__?: string;
	},
) {
	writeQuery({ ...options, client: window.GLOBAL_APOLLO_AGG_CLIENT });
}
